// Quotation / Proforma / Invoice profile page — the same "click in, see
// everything" experience DealDetailPage already gives Deals, applied to the
// three document types that share SalesOrderModel. One component serves all
// three since they're literally the same underlying record, discriminated
// by documentType.

import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft, Building2, User, Calendar, FileText, ExternalLink, Download, Send, CheckCircle2, GitBranch,
  Briefcase, Clock, Copy, XCircle, Link2, ArrowRight, Wallet,
} from 'lucide-react';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Button } from '@/shared/components/ui/Button';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { DocumentActions, useDocumentTransitions } from '@/shared/components/documents/DocumentActions';
import { DocumentChainActions } from '@/shared/components/documents/DocumentChainActions';
import { ConnectedLifecycleStageBar } from '@/shared/components/documents/ConnectedLifecycleStageBar';
import { LostReasonModal } from '@/features/deals/components/LostReasonModal';
import { useSendSalesDoc, describeSendResult, sendErrorText } from '@/shared/hooks/useDocumentSend';
import { docCurrency, formatDocTotal, docInrEquivalent, formatINR } from '@/shared/utils/currency';
import { ratePerBaseUnit } from '@/shared/utils/ratePerBaseUnit';
import { cn } from '@/shared/utils/utils';
import { useSalesDoc } from '../hooks/useSalesDoc';
import { useDeal } from '@/features/deals/hooks/useDeals';
import { STAGE_LABELS, formatMoney } from '@/features/deals/constants';
import { FollowupPanel } from '@/features/activity/components/TimelinePanel';
import { ActivityTimeline } from '@/features/activity/components/ActivityTimeline';
import { useEntityActivity } from '@/features/activity/hooks/useActivity';
import type { TimelineEntityType } from '@/features/activity/types';
import type { ParentType } from '@/features/activity/hooks/useFollowups';
import { ROUTES } from '@/routes/paths';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { useUser } from '@/shared/hooks/useAuth';
import { InvoicePoPanel } from '@/features/quotations/components/InvoicePoPanel';
import { useBillToPreview } from '@/features/quotations/hooks/useQuotationForm';
import { RecordAdvanceModal } from '@/features/sales/components/RecordAdvanceModal';

const DOC_LABEL: Record<string, string> = {
  quotation: 'Quotation',
  proforma: 'Proforma Invoice',
  direct_sale: 'Invoice',
  sales_order: 'Sales Order',
};

const LIST_ROUTE: Record<string, string> = {
  quotation: ROUTES.quotations,
  proforma: ROUTES.proforma,
  direct_sale: ROUTES.invoices,
  sales_order: ROUTES.salesOperations,
};

// The `type` query param QuotationFormPage's create form reads to decide
// which document it's creating (see `cloneFrom` there). Only Quotation is
// cloneable from this page: cloning an Invoice or Proforma
// creates a brand-new, unlinked draft (routed through this same form) that
// looks like a second in-flight invoice for a sale already in progress —
// confirmed live as a real duplicate. A Sales Order has no "new" form of
// its own to open into either.
const CLONE_TYPE_PARAM: Record<string, string> = {
  quotation: 'quotation',
};

// The Followup system's parent-type names (db-followups.js PARENT_TYPES)
// don't exactly match SalesOrder's documentType — 'direct_sale' is called
// 'invoice' there.
const FOLLOWUP_PARENT_TYPE: Record<string, ParentType> = {
  quotation: 'quotation',
  proforma: 'proforma',
  direct_sale: 'invoice',
};

// Where the "Document chain" block below can actually send someone —
// convertedFromType/linkedDocuments.docType carry the raw SalesOrder
// documentType (transitions.js), which only some of these have a page for
// (invoice_request and delivery_challan live on Accounts/Dispatch screens
// this page doesn't know the id shape for) — those show as plain text
// instead of a guessed, possibly-wrong link.
const CHAIN_ROUTE: Record<string, string> = {
  quotation: '/quotations',
  proforma: '/proforma',
  direct_sale: '/invoices',
  sales_order: '/sales-orders',
};
const CHAIN_LABEL: Record<string, string> = {
  quotation: 'Quotation',
  proforma: 'Proforma',
  direct_sale: 'Invoice',
  sales_order: 'Sales Order',
  invoice_request: 'Invoice Request',
  delivery_challan: 'Delivery Challan',
};

function useLeadSummary(leadId: string | null) {
  return useQuery({
    queryKey: ['lead', 'summary', leadId],
    queryFn: async () => {
      const { data } = await api.get<any>(`/api/leads/${leadId}`);
      return data?.lead || data;
    },
    enabled: !!leadId,
  });
}

export function SalesDocDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUser = useUser();
  const isSystemAdminUser = ['admin', 'superadmin'].includes(currentUser?.role || '');

  const { data: doc, isLoading, isError } = useSalesDoc(id ?? null);
  const { data: deal } = useDeal(doc?.crmDealId ?? null);
  const { data: lead } = useLeadSummary(!doc?.crmDealId ? (doc?.crmLeadId ?? null) : null);
  const sendDoc = useSendSalesDoc();

  const handleSend = () => {
    if (!doc) return;
    sendDoc.mutate(doc._id, {
      onSuccess: (data) => {
        if (data.ok) toast.success(describeSendResult(data.result));
        else toast.error(describeSendResult(data.result));
      },
      onError: (err) => toast.error(sendErrorText(err, 'Could not send the document.')),
    });
  };

  // ── The two steps this page used to conflate ──────────────────────────────
  //
  // A DRAFT is the sales rep's own working copy: edit the prices, fix the
  // packing, get it right. Nothing leaves the building.
  //
  //   CONFIRM  — "I have finished editing." Issues the document and gives it
  //              its number. Ours, not the customer's.
  //   SEND     — posts it to the customer by email/WhatsApp.
  //   ACCEPTED — the customer said yes. THIS is what unlocks the proforma and
  //              tax-invoice conversions.
  //
  // Previously one "Confirm" button did the last of those and was shown on a
  // draft, so a brand-new quotation could be marked customer-accepted before it
  // had a number, let alone before anyone had seen it — which unlocked billing
  // for a price no customer had ever been quoted.
  const qc = useQueryClient();
  const refreshDoc = () => {
    qc.invalidateQueries({ queryKey: ['sales-doc'] });
    qc.invalidateQueries({ queryKey: ['quotations'] });
    qc.invalidateQueries({ queryKey: ['proforma'] });
    // DocumentActions reads ['document-transitions', docType, docId] — not
    // ['documents','transitions']. Invalidating the wrong key is why the
    // conversion buttons only appeared after a manual page refresh.
    qc.invalidateQueries({ queryKey: ['document-transitions'] });
  };

  // Draft -> issued. The NUMBER is not allocated here: allocateNumber runs at
  // creation (see POST /api/sales-ops/orders), because a quotation is a
  // document a customer may see immediately, so it needs a stable number from
  // the moment it exists. Issuing is about the document being final, not about
  // it acquiring an identity.
  const issueDoc = useMutation({
    mutationFn: async (docId: string) => (await api.post(`/api/sales-ops/orders/${docId}/issue`)).data,
    onSuccess: (data: { orderNumber?: string }) => {
      refreshDoc();
      toast.success(
        data?.orderNumber
          ? `${data.orderNumber} issued. You can send it now.`
          : 'Issued. You can send it now.',
      );
    },
    onError: (err) => toast.error(sendErrorText(err, 'Could not confirm this document.')),
  });

  /** The customer accepted it. Gates the proforma/invoice conversions. */
  const acceptDoc = useMutation({
    mutationFn: async (docId: string) => (await api.post(`/api/sales-ops/orders/${docId}/confirm`)).data,
    onSuccess: () => {
      refreshDoc();
      toast.success('Recorded as accepted by the customer.');
    },
    onError: (err) => toast.error(sendErrorText(err, 'Could not record acceptance.')),
  });

  // Acceptance is allowed on a document this system never sent — customers
  // accept by phone, on WhatsApp from a personal number, or across a counter,
  // and email/WhatsApp are not even configured here yet. But it is worth a
  // pause, because "accepted" is what unlocks billing.
  const [acceptConfirmOpen, setAcceptConfirmOpen] = useState(false);
  const requestAccept = () => {
    if (!doc) return;
    if (!doc.lastSentAt) { setAcceptConfirmOpen(true); return; }
    acceptDoc.mutate(doc._id);
  };

  const isSalesDoc = doc?.documentType === 'quotation' || doc?.documentType === 'proforma';

  // Whether "Create Tax Invoice" may be offered, and why not when it may not.
  // Same query key DocumentActions uses, so the two share one request and one
  // cache entry rather than asking the server the same question twice.
  const { data: transitionData } = useDocumentTransitions(doc?.documentType ?? '', doc?._id ?? null);
  const taxInvoice = transitionData?.taxInvoice ?? null;

  // "Revise" is still available for negotiation history, but Edit now keeps
  // the same document number because dispatch/accounts must see updates on the
  // final document instead of a duplicate sibling.
  const reviseDoc = useMutation({
    mutationFn: async (docId: string) =>
      (await api.post(`/api/sales-ops/orders/${docId}/revise`)).data as { revision: { _id: string; orderNumber: string; documentType?: string }; revisionNumber: number },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['sales-doc'] });
      qc.invalidateQueries({ queryKey: ['quotations'] });
      qc.invalidateQueries({ queryKey: ['proforma'] });
      toast.success(`Revision ${data.revisionNumber} created as ${data.revision.orderNumber}.`);
      navigate(`${data.revision.documentType === 'proforma' ? '/proforma' : '/quotations'}/${data.revision._id}`);
    },
    onError: (err) => toast.error(sendErrorText(err, 'Could not revise this document.')),
  });

  // A never-issued draft (a stray Clone, a false start) can be set aside by
  // whoever made it. The server refuses a revision or anything already used.
  const [discardOpen, setDiscardOpen] = useState(false);
  const discardDraft = useMutation({
    mutationFn: async (docId: string) => (await api.post(`/api/sales-ops/orders/${docId}/discard-draft`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-doc'] });
      qc.invalidateQueries({ queryKey: ['quotations'] });
      qc.invalidateQueries({ queryKey: ['proforma'] });
      toast.success('Draft discarded.');
      setDiscardOpen(false);
    },
    onError: (err) => { setDiscardOpen(false); toast.error(sendErrorText(err, 'Could not discard this draft.')); },
  });

  const [lostModalOpen, setLostModalOpen] = useState(false);
  const [advanceModalOpen, setAdvanceModalOpen] = useState(false);
  const markLostDoc = useMutation({
    mutationFn: async (payload: { lostReasonId: string; lostReasonNote: string; closeDeal: boolean }) =>
      (await api.post(`/api/sales-ops/orders/${doc?._id}/mark-lost`, payload)).data,
    onSuccess: (data) => {
      refreshDoc();
      qc.invalidateQueries({ queryKey: ['pipeline'] });
      qc.invalidateQueries({ queryKey: ['deal'] });
      qc.invalidateQueries({ queryKey: ['deals'] });
      setLostModalOpen(false);
      toast.success(data?.dealClosed
        ? 'Deal and document marked as Closed Lost with reason.'
        : 'Document marked as lost. The deal stays open.');
    },
    onError: (err) => toast.error(sendErrorText(err, 'Could not mark document as lost.')),
  });

  const activityType = (doc?.documentType || 'quotation') as TimelineEntityType;
  const { data: activity = [], isLoading: activityLoading, isError: activityError } = useEntityActivity(activityType, id ?? null);

  const customerName = useMemo(() => {
    if (!doc) return '';
    return doc.customerName || (typeof doc.customerId === 'object' ? doc.customerId?.name : '') || 'No client';
  }, [doc]);

  const effectiveContactId = (doc?.crmContactId as string)
    || (typeof doc?.customerId === 'object' ? (doc?.customerId as any)?._id : (doc?.customerId as string || null))
    || (deal?.contact_id as any)?._id
    || (deal?.contact_id as string)
    || null;

  const { data: contactBillToPreview } = useBillToPreview(effectiveContactId);

  const syncContactAddressMutation = useMutation({
    mutationFn: async () => (await api.post(`/api/sales-ops/orders/${doc?._id}/sync-contact-address`)).data,
    onSuccess: () => {
      refreshDoc();
      toast.success('Document updated with the latest address and details from Contacts.');
    },
    onError: (err) => toast.error(sendErrorText(err, 'Could not sync contact address.')),
  });

  const addressDiff = useMemo(() => {
    if (!doc || !contactBillToPreview?.billTo?.billingAddress) return null;
    const cAddr = contactBillToPreview.billTo.billingAddress;
    const docAddr = (doc.billingAddress || {}) as Record<string, any>;

    const cStreet = String(cAddr.street || '').trim();
    const dStreet = String(docAddr.street || '').trim();
    const cCity = String(cAddr.city || '').trim();
    const dCity = String(docAddr.city || '').trim();
    const cZip = String(cAddr.zipCode || '').trim();
    const dZip = String(docAddr.zipCode || '').trim();

    if (!cStreet && !cCity) return null;

    const normalize = (s: string) => s.toLowerCase().replace(/[\s,.-]+/g, ' ').trim();
    const streetDiffers = normalize(cStreet) !== normalize(dStreet);
    const cityDiffers = normalize(cCity) !== normalize(dCity);
    const zipDiffers = cZip && dZip && cZip !== dZip;

    if (streetDiffers || cityDiffers || zipDiffers) {
      return {
        contactAddressFormatted: [cStreet, cCity, cAddr.state, cAddr.zipCode].filter(Boolean).join(', '),
        docAddressFormatted: [dStreet, dCity, docAddr.state, docAddr.zipCode].filter(Boolean).join(', ') || 'None',
      };
    }
    return null;
  }, [doc, contactBillToPreview]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <Spinner />
      </div>
    );
  }

  if (isError || !doc) {
    return (
      <div className="p-8 bg-slate-50 min-h-screen">
        <div className="max-w-xl mx-auto">
          <EmptyState
            title="Document Not Found"
            description="The requested document could not be loaded or you may not have access."
            action={
              <Button onClick={() => navigate(ROUTES.quotations)}>
                <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  const docLabel = DOC_LABEL[doc.documentType] || doc.documentType;
  const listRoute = LIST_ROUTE[doc.documentType] || ROUTES.quotations;
  // Amounts of a USD/EUR document are in that currency, not rupees.
  const docMoney = (n?: number | null) => formatMoney(n, docCurrency(doc as never));

  return (
    <div className="space-y-6">
      {/* ── TOP BREADCRUMB & ACTION BAR ── */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200/80 pb-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(listRoute)}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 shadow-xs transition-all"
            title={`Back to ${docLabel}s`}
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <span className="rounded-md bg-info/10 px-2 py-0.5 font-mono text-[11px] font-bold text-info-text border border-info/25">
                {doc.orderNumber || doc._id.slice(-6)}
              </span>
              <span className="text-slate-300">•</span>
              <span className="text-xs font-semibold text-slate-500">{docLabel}</span>
              <Badge className="text-[10px] font-bold uppercase bg-slate-100 text-slate-700">{doc.status}</Badge>
              {doc.customerPoNumber && (
                <span className="rounded-md bg-success/10 px-2 py-0.5 text-[11px] font-bold text-success-text border border-success/25 flex items-center gap-1">
                  PO: {doc.customerPoNumber}
                  {doc.customerPoDate && <span className="font-normal text-success-text">({new Date(doc.customerPoDate).toLocaleDateString('en-IN')})</span>}
                </span>
              )}
            </div>
            <h1 className="mt-0.5 text-2xl font-bold text-slate-900 tracking-tight">{customerName}</h1>
            {/* A revision explains itself: which attempt this is, and whether
                it has already been replaced by a later one. */}
            {(doc.revisionNumber > 1 || doc.status === 'superseded') && (
              <p className="mt-1 text-[11px] font-semibold text-slate-500">
                {doc.revisionNumber > 1 && `Revision ${doc.revisionNumber}`}
                {doc.revisionNumber > 1 && doc.status === 'superseded' && ' · '}
                {doc.status === 'superseded' && (
                  <span className="text-warning-text">Replaced by a newer revision</span>
                )}
              </p>
            )}
          </div>
        </div>

        {/* Quotation and Proforma share this whole lifecycle. */}
        <div className="flex flex-wrap items-center gap-2.5">
          {/* STEP 1 — finish editing. Issues the document and numbers it.
              Only ever shown on a draft. */}
          {isSalesDoc && doc.status === 'draft' && (
            <Button
              size="sm"
              onClick={() => issueDoc.mutate(doc._id)}
              loading={issueDoc.isPending}
              className="rounded-xl text-xs font-bold flex items-center gap-1.5"
              title="Finish editing and issue this document, so it can be sent"
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Confirm
            </Button>
          )}

          {/* STEP 3 — the customer accepted. Unlocks the conversions, so it is
              never offered on a draft: there would be nothing to accept. */}
          {isSalesDoc && ['quotation', 'proforma'].includes(doc.status) && (
            <Button
              size="sm"
              variant="secondary"
              onClick={requestAccept}
              loading={acceptDoc.isPending}
              className="rounded-xl text-xs font-bold flex items-center gap-1.5"
              title="Record that the customer accepted this document"
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Mark as Accepted
            </Button>
          )}
          {isSalesDoc
            && !['invoiced', 'partially_paid', 'paid', 'superseded'].includes(doc.status) && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => reviseDoc.mutate(doc._id)}
              loading={reviseDoc.isPending}
              className="rounded-xl text-xs font-bold flex items-center gap-1.5"
              title="Send the client another version — keeps this one as history"
            >
              <GitBranch className="h-3.5 w-3.5" /> Revise
            </Button>
          )}
          {/* STEP 2 — post it to the customer. A draft has no number and was
              never finalised, so it cannot go out; the server refuses it too. */}
          {isSalesDoc && (
            <Button
              size="sm"
              variant={doc.status === 'draft' ? 'outline' : undefined}
              disabled={doc.status === 'draft'}
              onClick={handleSend}
              loading={sendDoc.isPending}
              title={doc.status === 'draft'
                ? 'Confirm this document first to issue it, then it can be sent.'
                : 'Email / WhatsApp this document to the client'}
              className="rounded-xl text-xs font-bold flex items-center gap-1.5"
            >
              <Send className="h-3.5 w-3.5" /> Send
            </Button>
          )}
          <a
            href={`/api/documents/quotation/${doc._id}?format=pdf`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-bold text-slate-700 shadow-xs hover:bg-slate-50 transition-all"
          >
            <Download className="h-3.5 w-3.5" /> Download
          </a>
          {/* STEP: Record Advance Amount */}
          {!['cancelled', 'expired', 'superseded'].includes(doc.status) && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setAdvanceModalOpen(true)}
              className="rounded-xl text-xs font-bold flex items-center gap-1.5 border-emerald-600/30 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-all shadow-xs"
              title="Record advance payment received from customer"
            >
              <Wallet className="h-3.5 w-3.5" /> Record Advance
            </Button>
          )}
          {/* Opens the SAME create form this document itself was raised on,
              pre-filled with everything — client, lines, notes, terms — via
              `cloneFrom`. Nothing is created until Save; the new document
              gets its own next number and today's date because it goes
              through the ordinary create path (POST /api/sales-ops/orders),
              never touching the source document's own number. */}
          {CLONE_TYPE_PARAM[doc.documentType] && (
            <button
              type="button"
              onClick={() => navigate(`${ROUTES.quotationNew}?cloneFrom=${doc._id}&type=${CLONE_TYPE_PARAM[doc.documentType]}`)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-bold text-slate-700 shadow-xs hover:bg-slate-50 transition-all"
              title={`Start a new ${docLabel.toLowerCase()} pre-filled with everything from this one`}
            >
              <Copy className="h-3.5 w-3.5" /> Clone
            </button>
          )}
          {/* Same-number edits stay available after issue/finalization so an
              updated invoice/proforma/quotation refreshes the whole system and
              the dispatch copy instead of creating a duplicate document. */}
          {!['cancelled', 'expired', 'superseded'].includes(doc.status) && (
            <button
              type="button"
              onClick={(e) => {
                if (doc.documentType === 'sales_order' && !isSystemAdminUser) {
                  e.preventDefault();
                  toast.error('Editing a Sales Order requires Admin or Superadmin approval.');
                  return;
                }
                navigate(
                  doc.documentType === 'quotation'
                    ? `/quotations/${doc._id}/edit`
                    : `${listRoute}/${doc._id}/edit`,
                );
              }}
              className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-body-sm font-semibold shadow-surface-1 transition-colors ${
                doc.documentType === 'sales_order' && !isSystemAdminUser
                  ? 'bg-primary/60 text-on-primary cursor-not-allowed'
                  : 'bg-primary text-on-primary hover:bg-primary-dim'
              }`}
              title={doc.documentType === 'sales_order' && !isSystemAdminUser ? 'Editing a Sales Order requires Admin/Superadmin approval' : ''}
            >
              Edit
            </button>
          )}
          {doc.documentType === 'sales_order' && !isSystemAdminUser && !['cancelled', 'expired', 'superseded'].includes(doc.status) && (
            <p className="text-[11px] text-warning-text font-medium">Sales Order edits are queued for Admin/Superadmin approval</p>
          )}
          {doc.status === 'draft' && !doc.revisionOfId && doc.documentType !== 'sales_order' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDiscardOpen(true)}
              className="rounded-xl text-xs font-bold flex items-center gap-1.5 text-slate-600"
              title="Set this never-issued draft aside (it is cancelled, not deleted)"
            >
              <XCircle className="h-3.5 w-3.5" /> Discard draft
            </Button>
          )}
          {isSalesDoc && !['cancelled', 'invoiced', 'fulfilled', 'paid', 'superseded'].includes(doc.status) && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setLostModalOpen(true)}
              className="rounded-xl text-xs font-bold flex items-center gap-1.5 text-danger-text border-danger/25 hover:bg-danger/10 hover:text-danger-text transition-all"
              title="Mark this deal and quotation as Lost with reason"
            >
              <XCircle className="h-3.5 w-3.5" /> Mark Lost
            </Button>
          )}
          <DocumentActions docType={doc.documentType} docId={doc._id} />
          {/* The end of the document chain, beside the other conversions
              rather than as a separate block below them — it is one action
              among several, not the page's primary one.

              WHEN IT SHOWS AT ALL is the server's call (taxInvoice, from
              GET /api/documents/transitions/:type/:id), not this page's: it
              used to render on anything not outright cancelled, so it offered
              to bill a quotation nobody had accepted, one that had already
              become a proforma, and one that had already been invoiced as a
              draft. A quotation with a proforma behind it keeps a DISABLED
              button carrying the reason — "invoice from the proforma" is
              information the rep needs; the other blocks are just noise, so
              the button goes. */}
          {taxInvoice?.allowed && (
            <DocumentChainActions
              inline
              size="sm"
              documentId={doc._id}
              documentType={doc.documentType as 'quotation' | 'proforma' | 'direct_sale'}
              documentNumber={doc.orderNumber}
            />
          )}
          {taxInvoice?.blockedBy === 'proforma_exists' && (
            <span title={taxInvoice.reason}>
              <Button size="sm" variant="outline" disabled className="rounded-xl text-xs font-bold">
                Create Tax Invoice
              </Button>
            </span>
          )}
        </div>
      </div>

      {/* ── SALES LIFECYCLE STAGES (Deal -> Sample -> Quotation -> Proforma -> Tax Invoice) ── */}
      <ConnectedLifecycleStageBar
        documentType={doc.documentType}
        documentStatus={doc.status}
        dealStage={deal?.stage}
        dealTitle={deal?.title}
        hasSample={Boolean((doc as any).samplingRequestId || doc.notes?.toLowerCase().includes('sample'))}
        sampleCode={(doc as any).samplingRequestCode}
        lostReasonName={(typeof doc.lostReasonId === 'object' ? doc.lostReasonId?.name : undefined) || (doc as any).lostReasonName}
        lostReasonNote={doc.lostReasonNote}
        onMarkLost={() => setLostModalOpen(true)}
      />

      {/* ── DOCUMENT CHAIN — where this record came from and what it became.
          Previously only visible on the printed PDF (Quotation no. / Prepared
          by rows); admin/superadmin had no way to trace quotation -> proforma
          -> invoice from the app itself. Renders nothing when the document
          has no chain at all — a quotation raised with nothing behind or
          ahead of it is a normal, common case, not a missing link. ── */}
      {(doc.convertedFromNumber || (doc.linkedDocuments && doc.linkedDocuments.length > 0) || doc.accountsInvoiceId || doc.formalInvoiceNumber) && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200/80 bg-white px-4 py-3 text-xs shadow-xs">
          <span className="flex items-center gap-1.5 font-bold uppercase tracking-wider text-slate-400">
            <Link2 className="h-3.5 w-3.5" /> Document chain
          </span>
          {doc.convertedFromNumber && (
            <>
              {CHAIN_ROUTE[doc.convertedFromType || ''] ? (
                <Link
                  to={`${CHAIN_ROUTE[doc.convertedFromType || '']}/${doc.convertedFromId}`}
                  className="rounded-lg bg-slate-50 px-2 py-1 font-semibold text-slate-700 hover:bg-slate-100 hover:text-primary"
                >
                  {CHAIN_LABEL[doc.convertedFromType || ''] || doc.convertedFromType} {doc.convertedFromNumber}
                </Link>
              ) : (
                <span className="rounded-lg bg-slate-50 px-2 py-1 font-semibold text-slate-700">
                  {CHAIN_LABEL[doc.convertedFromType || ''] || doc.convertedFromType} {doc.convertedFromNumber}
                </span>
              )}
              <ArrowRight className="h-3 w-3 text-slate-300" />
              <span className="rounded-lg bg-info/10 px-2 py-1 font-semibold text-info-text">
                This {CHAIN_LABEL[doc.documentType] || doc.documentType}
              </span>
            </>
          )}
          {doc.linkedDocuments?.map((link) => (
            <span key={link.docId} className="flex items-center gap-2">
              {!doc.convertedFromNumber && (
                <span className="rounded-lg bg-info/10 px-2 py-1 font-semibold text-info-text">
                  This {CHAIN_LABEL[doc.documentType] || doc.documentType}
                </span>
              )}
              <ArrowRight className="h-3 w-3 text-slate-300" />
              {CHAIN_ROUTE[link.docType] ? (
                <Link
                  to={`${CHAIN_ROUTE[link.docType]}/${link.docId}`}
                  className="rounded-lg bg-slate-50 px-2 py-1 font-semibold text-slate-700 hover:bg-slate-100 hover:text-primary"
                >
                  {CHAIN_LABEL[link.docType] || link.docType} {link.docNumber}
                </Link>
              ) : (
                <span className="rounded-lg bg-slate-50 px-2 py-1 font-semibold text-slate-700">
                  {CHAIN_LABEL[link.docType] || link.docType} {link.docNumber}
                </span>
              )}
            </span>
          ))}
          {(() => {
            const invoiceNo = (typeof doc.accountsInvoiceId === 'object' ? doc.accountsInvoiceId?.invoiceNumber : undefined)
              || doc.formalInvoiceNumber;
            return invoiceNo ? (
              <span className="flex items-center gap-2">
                <ArrowRight className="h-3 w-3 text-slate-300" />
                <span className="rounded-lg bg-success/10 px-2 py-1 font-semibold text-success-text">
                  Tax Invoice {invoiceNo}
                </span>
              </span>
            ) : null;
          })()}
        </div>
      )}

      {/* ── KEY METRIC CARDS ── */}
      <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-4">
        <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-xs">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Grand Total</span>
          <p className="mt-1.5 text-2xl font-bold tabular-nums text-slate-900 leading-tight">
            {formatDocTotal(doc as never)}
          </p>
          {docInrEquivalent(doc as never) && <p className="text-xs font-semibold text-slate-500">≈ {docInrEquivalent(doc as never)}</p>}
          <p className="mt-1 text-xs text-slate-500 font-medium">
            {/* The discount has to be in the sum, or "₹25,000 + tax ₹4,275"
                sits above a ₹28,025 total and reads as an arithmetic error. */}
            {docMoney(doc.subTotal || 0)}
            {Number((doc as { discountTotal?: number }).discountTotal) > 0 && <> − disc {docMoney(Number((doc as { discountTotal?: number }).discountTotal))}</>}
            {' '}+ tax {docMoney(doc.taxTotal || 0)}
          </p>
        </div>
        <div className="rounded-2xl border border-info/15 bg-info/10 p-4 shadow-xs">
          <span className="text-[11px] font-bold uppercase tracking-wider text-info-text">Status</span>
          <p className="mt-1.5 text-xl font-bold capitalize text-info-text leading-tight">
            {doc.status === 'draft' ? 'Draft' : doc.status === 'confirmed' ? 'Accepted' : doc.status.replace(/_/g, ' ')}
          </p>
          {/* Says where the document actually is in the lifecycle, in the
              order the steps happen: edited -> issued -> sent -> accepted.
              "Confirmed" alone used to mean two different things here. */}
          <p className="mt-1 text-xs font-medium text-info-text">
            {doc.status === 'draft'
              ? 'Still being edited — confirm it to issue it'
              : doc.confirmedAt
                ? `Customer accepted ${new Date(doc.confirmedAt).toLocaleDateString('en-IN')}`
                : doc.lastSentAt
                  ? `Sent ${new Date(doc.lastSentAt).toLocaleDateString('en-IN')} — awaiting the customer`
                  : 'Issued, not yet sent'}
          </p>
        </div>
        <div className="rounded-2xl border border-success/15 bg-success/10 p-4 shadow-xs">
          <span className="text-[11px] font-bold uppercase tracking-wider text-success-text flex items-center gap-1">
            <Calendar className="h-3 w-3" /> Date
          </span>
          <p className="mt-1.5 text-xl font-bold tabular-nums text-success-text leading-tight">
            {new Date(doc.date).toLocaleDateString('en-IN')}
          </p>
          {doc.validUntil && (
            <p className="mt-1 text-xs text-success-text font-medium">Valid until {new Date(doc.validUntil).toLocaleDateString('en-IN')}</p>
          )}
        </div>
        <div className="rounded-2xl border border-purple-100 bg-purple-50/40 p-4 shadow-xs">
          <span className="text-[11px] font-bold uppercase tracking-wider text-purple-600">Line Items</span>
          <p className="mt-1.5 text-2xl font-bold tabular-nums text-purple-900 leading-tight">{doc.lines?.length || 0}</p>
          <p className="mt-1 text-xs text-purple-600 font-medium">
            Created by {typeof doc.createdBy === 'object' ? (doc.createdBy?.username || '').toUpperCase() || '—' : 'System'}
          </p>
        </div>
      </div>

      {/* ── ADVANCE PAYMENT & BALANCE STATUS CARD ── */}
      <div className="rounded-2xl border border-emerald-600/20 bg-gradient-to-r from-emerald-50/70 via-white to-emerald-50/40 p-4 shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-600/10 text-emerald-700 border border-emerald-600/20">
              <Wallet className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-800">Advance Received Status</span>
                {Number(doc.amountPaid || 0) >= Number(doc.grandTotal || 0) && Number(doc.grandTotal || 0) > 0 ? (
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-800 border border-emerald-300">
                    Fully Paid
                  </span>
                ) : Number(doc.amountPaid || 0) > 0 ? (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800 border border-amber-300">
                    Partially Received
                  </span>
                ) : (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                    No Advance Yet
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-600 mt-0.5">
                {Number(doc.amountPaid || 0) > 0
                  ? `${formatINR(doc.amountPaid)} advance received against order total of ${formatINR(doc.grandTotal)}`
                  : 'Customer advance payments recorded here will auto-adjust against linked tax invoices.'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="text-right">
              <span className="text-[11px] uppercase font-bold text-slate-400">Advance Collected</span>
              <p className="text-xl font-bold tabular-nums text-emerald-700">{formatINR(doc.amountPaid || 0)}</p>
            </div>
            <div className="text-right border-l border-slate-200 pl-6">
              <span className="text-[11px] uppercase font-bold text-slate-400">Balance Due</span>
              <p className={cn("text-xl font-bold tabular-nums", (Number(doc.balanceDue ?? ((doc.grandTotal || 0) - (doc.amountPaid || 0)))) > 0 ? "text-amber-700" : "text-emerald-700")}>
                {formatINR(Math.max(0, Number(doc.balanceDue ?? ((doc.grandTotal || 0) - (doc.amountPaid || 0)))))}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => setAdvanceModalOpen(true)}
              className="rounded-xl text-xs font-bold gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white shadow-xs ml-2"
            >
              <Wallet className="h-3.5 w-3.5" />
              {Number(doc.amountPaid || 0) > 0 ? 'Update Advance' : 'Record Advance'}
            </Button>
          </div>
        </div>

        {/* Payment history list if payments exist */}
        {Array.isArray((doc as any).payments) && (doc as any).payments.length > 0 && (
          <div className="mt-3 pt-3 border-t border-emerald-600/10 space-y-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-900 block">
              Payment Receipts ({(doc as any).payments.length})
            </span>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {(doc as any).payments.map((p: any, idx: number) => (
                <div key={p._id || idx} className="rounded-lg bg-white/80 border border-emerald-600/15 p-2 text-xs flex justify-between items-center">
                  <div>
                    <span className="font-bold text-slate-800">{formatINR(p.amount)}</span>
                    <span className="text-[11px] text-slate-500 ml-1.5 capitalize font-medium">({(p.method || 'cash').replace('_', ' ')})</span>
                    {p.reference && <p className="text-[10px] text-slate-500 font-mono">Ref: {p.reference}</p>}
                    {p.notes && <p className="text-[10px] text-slate-600 italic">{p.notes}</p>}
                  </div>
                  <div className="text-right text-[11px] text-slate-500 font-medium">
                    {p.date ? new Date(p.date).toLocaleDateString('en-IN') : '—'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Customer PO — add or change it before invoicing (client, 2026-10-05) ──
          Same panel the proforma / invoice edit screens show: saved on the
          pipeline record (or on this document when it has none), which is what
          the printed invoice, the challan and Dispatch read. */}
      {['quotation', 'proforma', 'sales_order'].includes(doc.documentType)
        && !['cancelled', 'superseded', 'expired'].includes(doc.status) && (
        <InvoicePoPanel recordId={doc.crmDealId || undefined} orderId={doc._id} />
      )}

      {/* ── LINKED DEAL / LEAD — where negotiation status actually lives ── */}
      {(doc.crmDealId || doc.crmLeadId) && (
        <div className="rounded-2xl border border-warning/25 bg-warning/10 p-5 shadow-xs">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 border-b border-warning/15 pb-3">
            <Briefcase className="h-4 w-4 text-warning-text" />
            Where this stands — linked {doc.crmDealId ? 'Deal' : 'Lead'}
          </h3>
          {doc.crmDealId ? (
            deal ? (
              <Link
                to={`/deals/${doc.crmDealId}`}
                className="mt-3 flex items-center justify-between rounded-xl border border-warning/25 bg-white p-3 hover:bg-warning/10 transition-colors"
              >
                <div>
                  <p className="text-sm font-bold text-slate-900">{deal.title}</p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    Stage: <span className="font-bold text-warning-text">{STAGE_LABELS[deal.stage] || deal.stage?.replace(/_/g, ' ')}</span>
                    {' · '}{formatMoney(deal.value, deal.currency)}
                  </p>
                </div>
                <ExternalLink className="h-4 w-4 text-warning-text shrink-0" />
              </Link>
            ) : (
              <p className="mt-3 text-xs text-slate-400">Loading linked deal…</p>
            )
          ) : lead ? (
            <div className="mt-3 flex items-center justify-between rounded-xl border border-warning/25 bg-white p-3">
              <div>
                <p className="text-sm font-bold text-slate-900">{lead.name}</p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {lead.phone || ''}{lead.pipeline_stage ? ` · ${lead.pipeline_stage}` : ''} — not yet converted to a Deal
                </p>
              </div>
            </div>
          ) : (
            <p className="mt-3 text-xs text-slate-400">Loading linked lead…</p>
          )}
        </div>
      )}

      {doc.documentType === 'sales_order' && doc.dispatchProgress && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="font-bold">Dispatch status: {doc.dispatchProgress.status.replaceAll('_', ' ')}</h2>
          {doc.dispatchProgress.items.map((line, index) => <p key={index} className="mt-2 text-sm">{line.description}: {line.dispatchedQuantity} {line.unit} dispatched · {line.pendingQuantity} pending</p>)}
        </section>
      )}
      {/* ── MAIN 2-COLUMN DETAIL LAYOUT ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr,0.9fr]">
        {/* LEFT: CUSTOMER, LINE ITEMS, NOTES */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-xs">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <User className="h-4 w-4 text-slate-600" />
                Customer & Addresses
              </h3>
              {isSalesDoc && !['invoiced', 'cancelled', 'superseded'].includes(doc.status) && effectiveContactId && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => syncContactAddressMutation.mutate()}
                  loading={syncContactAddressMutation.isPending}
                  className="text-xs h-7 px-2.5 text-info-text border-info/25 hover:bg-info/10"
                  title="Pull latest contact address, phone, email, and GSTIN from Contacts"
                >
                  Sync from Contact
                </Button>
              )}
            </div>

            {/* If contact has a different address, show an explicit banner with 1-click update */}
            {isSalesDoc && addressDiff && !['invoiced', 'cancelled', 'superseded'].includes(doc.status) && (
              <div className="mt-3 rounded-xl border border-warning/25 bg-warning/10 p-3 text-xs text-warning-text flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2.5">
                <div>
                  <div className="font-bold flex items-center gap-1.5 text-warning-text">
                    <span>⚠️ Contact address has been updated</span>
                  </div>
                  <div className="text-[11px] text-warning-text mt-0.5">
                    Latest in Contacts: <span className="font-semibold">{addressDiff.contactAddressFormatted}</span>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => syncContactAddressMutation.mutate()}
                  loading={syncContactAddressMutation.isPending}
                  className="shrink-0 text-xs h-7 px-3 bg-warning-text hover:bg-warning-text/90 text-on-primary font-medium shadow-none"
                >
                  Update Quotation Address
                </Button>
              </div>
            )}
            <div className={`mt-3 grid grid-cols-1 ${doc.shippingAddress ? 'sm:grid-cols-2' : ''} gap-4 text-xs text-slate-700`}>
              <div className="space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Bill To</span>
                <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5 text-slate-400" /> {customerName}
                </div>
                {doc.customerPhone && <div>Phone: {doc.customerPhone}</div>}
                {doc.customerEmail && <div>Email: {doc.customerEmail}</div>}
                {doc.customerGstin && <div>GSTIN: {doc.customerGstin}</div>}
                {doc.billingAddress && typeof doc.billingAddress === 'object' && (
                  <div className="text-slate-500">
                    {[doc.billingAddress.street, doc.billingAddress.city, doc.billingAddress.state, doc.billingAddress.zipCode].filter(Boolean).join(', ')}
                  </div>
                )}
              </div>
              {doc.shippingAddress && (
                <div className="space-y-1 sm:border-l sm:border-slate-100 sm:pl-4">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-info-text">Ship To</span>
                  <div className="font-semibold text-slate-900">
                    {doc.shippingAddress.companyName || doc.shippingAddress.name || customerName}
                    {doc.shippingAddress.companyName && doc.shippingAddress.name && doc.shippingAddress.name !== doc.shippingAddress.companyName ? ` (Attn: ${doc.shippingAddress.name})` : ''}
                  </div>
                  {doc.shippingAddress.phone && <div>Phone: {doc.shippingAddress.phone}</div>}
                  {doc.shippingAddress.email && <div>Email: {doc.shippingAddress.email}</div>}
                  {doc.shippingAddress.gstin && <div>GSTIN: {doc.shippingAddress.gstin}</div>}
                  <div className="text-slate-500">
                    {[doc.shippingAddress.street, doc.shippingAddress.city, doc.shippingAddress.state, doc.shippingAddress.zipCode].filter(Boolean).join(', ')}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-xs">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-3">
              <FileText className="h-4 w-4 text-info-text" />
              Line Items
            </h3>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="text-slate-400 border-b border-slate-100">
                    <th className="py-1.5 pr-3">Item</th>
                    <th className="py-1.5 pr-3">HSN/SAC</th>
                    <th className="py-1.5 pr-3 text-right">Qty</th>
                    <th className="py-1.5 pr-3">Packing</th>
                    <th className="py-1.5 pr-3">Size</th>
                    <th className="py-1.5 pr-3 text-right">Rate</th>
                    <th className="py-1.5 pr-3 text-right">Price / Pack</th>
                    <th className="py-1.5 pr-3 text-right">Tax %</th>
                    <th className="py-1.5 pr-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(doc.lines || []).map((l, i) => (
                    <tr key={i} className="border-b border-slate-50 last:border-0">
                      <td className="py-2 pr-3 font-medium text-slate-800">{l.description}</td>
                      <td className="py-2 pr-3 text-slate-500">{l.hsnSac || '—'}</td>
                      <td className="py-2 pr-3 text-right">{l.quantity}</td>
                      <td className="py-2 pr-3 text-slate-500">{l.unit || '—'}</td>
                      <td className="py-2 pr-3 text-slate-500">{l.packSize || '—'}</td>
                      <td className="py-2 pr-3 text-right">{ratePerBaseUnit(l.unitPrice, l) || (l.qtyPerPack && l.qtyPerPack > 1 ? docMoney(l.unitPrice / l.qtyPerPack) : docMoney(l.unitPrice))}</td>
                      <td className="py-2 pr-3 text-right font-medium">{docMoney(l.unitPrice)}</td>
                      <td className="py-2 pr-3 text-right">{(l.cgstRate || 0) + (l.sgstRate || 0) + (l.igstRate || 0)}</td>
                      <td className="py-2 pr-3 text-right font-bold">{docMoney(l.lineTotal || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {doc.notes && (
            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-xs">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-3">
                <FileText className="h-4 w-4 text-slate-600" />
                Notes / Terms
              </h3>
              <p className="mt-3 whitespace-pre-wrap text-xs leading-relaxed text-slate-700">{doc.notes}</p>
            </div>
          )}
        </div>

        {/* RIGHT: FOLLOW-UPS & ACTIVITY */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-xs">
            <FollowupPanel parentType={FOLLOWUP_PARENT_TYPE[doc.documentType] || 'quotation'} parentId={doc._id} />
          </div>

          <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-xs">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-3">
              <Clock className="h-4 w-4 text-indigo-600" />
              Activity Stream
            </h3>
            <div className="mt-3">
              <ActivityTimeline
                items={activity}
                isLoading={activityLoading}
                isError={activityError}
                showActor
                emptyTitle="No activity yet"
                emptyDescription="Creation, edits, confirmations and conversions will be recorded here."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Acceptance is permitted on a document this system never sent — real
          acceptances arrive by phone, from a personal WhatsApp, or across a
          counter, and email/WhatsApp are not configured here yet. It is still
          worth one deliberate pause, because acceptance is what unlocks
          billing. */}
      <ConfirmDialog
        open={acceptConfirmOpen}
        title="This document was never sent from here"
        message={
          <>
            There is no record of {doc.orderNumber || 'this document'} being emailed or sent on
            WhatsApp from this system. Record the customer&apos;s acceptance anyway?
            <span className="mt-2 block text-xs text-slate-500">
              Marking it accepted is what allows a proforma or tax invoice to be raised against it.
            </span>
          </>
        }
        confirmLabel="Yes, they accepted it"
        cancelLabel="Cancel"
        loading={acceptDoc.isPending}
        onConfirm={() => { setAcceptConfirmOpen(false); acceptDoc.mutate(doc._id); }}
        onCancel={() => setAcceptConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardOpen}
        title={`Discard draft ${doc.orderNumber || ''}?`}
        message="The draft is cancelled and kept for the record. Nothing else changes — the enquiry and any other quotation stay as they are."
        confirmLabel="Discard draft"
        cancelLabel="Keep it"
        loading={discardDraft.isPending}
        onConfirm={() => discardDraft.mutate(doc._id)}
        onCancel={() => setDiscardOpen(false)}
      />

      {lostModalOpen && doc && (
        <LostReasonModal
          deal={{ title: deal?.title || doc.orderNumber || `${docLabel} ${doc.orderNumber || ''}` } as any}
          onCancel={() => setLostModalOpen(false)}
          onConfirm={(payload) => markLostDoc.mutate(payload)}
          isSaving={markLostDoc.isPending}
          closeDealChoice={(doc.crmDealId || doc.crmLeadId)
            ? { dealTitle: deal?.title || lead?.title || 'the linked deal' }
            : undefined}
        />
      )}

      {advanceModalOpen && doc && (
        <RecordAdvanceModal
          open={advanceModalOpen}
          onClose={() => setAdvanceModalOpen(false)}
          orderId={doc._id}
          orderNumber={doc.orderNumber}
          documentType={doc.documentType}
          clientName={customerName}
          grandTotal={doc.grandTotal}
          currentAmountPaid={doc.amountPaid || 0}
          onSuccess={() => refreshDoc()}
        />
      )}
    </div>
  );
}

export default SalesDocDetailPage;
